<template>
  <fieldset>
    <SignupLegend :title="`Administrator of ${formData.name}`"/>
    <p>Select one person in your company, who should be made admin for controlling company budgets, inviting new users
      and allocating funds.</p>
    <div class="form-group row">
      <label class="col-sm-3 col-form-label">Admin</label>
      <div class="col-sm-9">
        <div class="form-check" v-for="(p, i) in people" :key="i">
          <input type="radio" :id="`admin${i}`" @change="selectAdmin(p)" class="form-check-input" name="admin" required
                 :value="p.name" v-model="formData.selectedAdmin">
          <label class="form-check-label" :for="`admin${i}`">{{p.name}}</label>
        </div>
        <div class="form-check">
          <input type="radio" id="adminNull" @change="selectAdmin" class="form-check-input" name="admin" required
                 value="newAdmin" v-model="formData.selectedAdmin">
          <label class="form-check-label" for="adminNull">somebody else</label>
          <div class="invalid-feedback">Company administrator is required.</div>
        </div>
      </div>
    </div>
    <div v-show="formData.selectedAdmin">
      <div class="form-group row">
        <label class="col-sm-3 col-form-label">Admin work email</label>
        <div class="col-sm-9">
          <input type="email" class="form-control" required :disabled="!formData.admin.changeEmail"
                 v-model="formData.admin.email">
          <div class="invalid-feedback">Valid email address is required.</div>
          <a class="float-right" @click="toggleChangeEmail" href="javascript:">{{!formData.admin.changeEmail ? 'change'
            :
            'revert'}}</a>
        </div>
      </div>
      <div class="form-group row" v-if="formData.admin.changeEmail">
        <label class="col-sm-3 col-form-label">Password</label>
        <div class="col-sm-9">
          <input type="password" class="form-control" placeholder="password" autocomplete="new-password" minlength="9"
                 required v-model="formData.admin.password">
          <div class="invalid-feedback">Password with at least 9 characters is required.</div>
        </div>
      </div>
      <div class="form-group row" v-show="newAdmin">
        <label class="col-sm-3 col-form-label">Admin first name</label>
        <div class="col-sm-9">
          <input type="text" class="form-control" placeholder="First name" minlength="2" required
                 v-model="formData.admin.firstName">
          <div class="invalid-feedback">Admin first name is required.</div>
        </div>
      </div>
      <div class="form-group row" v-show="newAdmin">
        <label class="col-sm-3 col-form-label">Admin last name</label>
        <div class="col-sm-9">
          <input type="text" class="form-control" placeholder="Last name" minlength="2" required
                 v-model="formData.admin.lastName">
          <div class="invalid-feedback">Admin last name is required.</div>
        </div>
      </div>

      <div class="form-group row">
        <label class="col-sm-3 col-form-label">Should admin receive an aio* business card?</label>
        <div class="col-sm-9">
          <div class="form-check">
            <input type="radio" id="own-card-yes" class="form-check-input" name="own-card" :value="true" required
                   v-model="formData.admin.wantsCard">
            <label class="form-check-label" for="own-card-yes">Yes, they should have their own card</label>
          </div>
          <div class="form-check">
            <input type="radio" id="own-card-no" class="form-check-input" name="own-card" :value="false"
                   v-model="formData.admin.wantsCard">
            <label class="form-check-label" for="own-card-no">No, they don't need one</label>
            <div class="invalid-feedback">Please select either 'Yes' or 'No'.</div>
          </div>
          <span id="card-error"></span>
        </div>
      </div>
      <!-- TODO: instead of asking for DOB here, sent out invitation for new admin (user-signup) -->
      <div v-if="showBirthdate">
        <hr>
        <div class="form-group row">
          <div class="col-sm-3">
            <label class="col-form-label">Admin's date of birth</label>
            <span id="dobInfo"><span class="info popup fas fa-info-circle"></span>
              <b-popover target="dobInfo" placement="bottom" triggers="hover focus">
                When somebody receives a card, it needs to be activated. Card services use date of birth as one way to keep the activation secure.
              </b-popover>
            </span>
          </div>
          <div class="col-sm-9">
            <BaseSelectDate @change="isEditing = true" v-model="formData.admin.dateOfBirth"/>
          </div>
        </div>
      </div>
      <hr>
      <div class="form-check form-group">
        <input class="form-check-input" type="checkbox" id="risk-policies" required
               v-model="formData.confirmRiskPolicies">
        <label class="form-check-label" for="risk-policies">We have appropriate risk-sensitive policies and procedures
          in place during recruitment of employees we intend to issue cards to.
          <span id="risk-policies-info"><span class="info popup fas fa-info-circle"></span>
          <b-popover target="risk-policies-info" placement="bottom" triggers="hover focus">
            Anti Money Laundering Regulations include:
            <ul>
              <li>Identity checks</li>
              <li>Employment history and reference checks</li>
              <li>Right to work check</li>
              <li>Criminal record check</li>
              <li>etc.</li>
            </ul>
          </b-popover>
        </span>
        </label>
        <div class="invalid-feedback">Confirmation is required.</div>
      </div>
      <div class="form-check form-group">
        <input class="form-check-input" type="checkbox" id="terms-conditions" required
               v-model="formData.confirmTermsConditions">
        <label class="form-check-label" for="terms-conditions">I have read the <a
          href="https://getaio.co.uk/privacy/" target="_blank">Privacy policy</a> and <a
          href="https://getaio.co.uk/terms-conditions/" target="_blank">Terms and Conditions</a> of aio* and I am
          authorised to accept those on behalf of {{formData.name}}.</label>
        <div class="invalid-feedback">Confirmation is required.</div>
      </div>
    </div>
    <hr>
    <BaseNaviButtons :beforeSubmit="setAdmin" :toSubmit="formData"/>
  </fieldset>
</template>

<script>
  import BaseNaviButtons from '@/components/BaseNaviButtons'
  import BaseSelectDate from '@/components/BaseSelectDate'
  import Person from '@/utils/Person'
  import SignupLegend from '@/components/SignupLegend'

  export default {
    components: {
      BaseNaviButtons,
      BaseSelectDate,
      SignupLegend
    },
    computed: {
      newAdmin () {
        return this.formData.selectedAdmin === 'newAdmin'
      },
      people () {
        let people = this.formData.companyDirectors.concat(this.formData.companyShareholders)
        return people.filter((p, i) => people.findIndex(p2 => p2.name === p.name) === i && !p.isCompany)
      },
      showBirthdate () {
        const isBirthdayValid = !isNaN(this.formData.admin.dateOfBirth.getFullYear())
        return (this.formData.admin.wantsCard && !isBirthdayValid) || this.isEditing
      }
    },
    data () {
      return {
        isEditing: false
      }
    },
    methods: {
      selectAdmin (admin) {
        this.formData.admin.firstName = admin.firstName ? admin.firstName : ''
        this.formData.admin.lastName = admin.lastName ? admin.lastName : ''
        this.formData.admin.dateOfBirth = admin.dateOfBirth ? admin.dateOfBirth : new Date('')
      },
      setAdmin () {
        this.formData.admin.dob = Person.getDobString(this.formData.admin)
        return Promise.resolve()
      },
      toggleChangeEmail () {
        this.formData.admin.changeEmail = !this.formData.admin.changeEmail
        if (this.formData.admin.changeEmail) {
          this.originalEmail = this.formData.admin.email
          this.originalPassword = this.formData.admin.password
        } else {
          this.formData.admin.email = this.originalEmail
          this.formData.admin.password = this.originalPassword
        }
        this.$forceUpdate()
      }
    },
    props: {
      formData: Object
    }
  }
</script>

<style lang="scss" scoped>
</style>
